import { AlertTriangle, CheckCircle2, CircleDashed, ListTodo, UserX } from 'lucide-react'
import { Link } from 'react-router-dom'

import { CompanySpine, UserAvatar } from '@/shared/components/common'
import { PageHeader } from '@/shared/components/common'
import { EmptyState, ErrorState, SkeletonCards, SkeletonRows } from '@/shared/components/data'
import { useT } from '@/shared/i18n'
import type { CompanyMetrics, OwnerMetrics } from '@/shared/api/contracts'

import { BarList } from '@/shared/components/charts'

import { useTeamMetrics } from './api'
import { MetricTile } from './components/MetricTile'
import { TrendsSection } from './components/TrendsSection'

/**
 * `/metrics` — cómo viene el equipo.
 *
 * Tres cortes de la misma realidad: el total, cada empresa y cada persona. Es
 * la única pantalla que mide **salida** y no sólo deuda: "entregados" y "tareas
 * cerradas" son los dos números que dicen que algo se movió. Sin ellos un
 * tablero de métricas se convierte en una lista de cosas que están mal, que a
 * los tres días nadie abre.
 *
 * No tiene filtros a propósito. El portafolio y la línea de tiempo son para
 * recortar; esto es para leer el estado completo de un vistazo.
 */
export function MetricsPage() {
  const t = useT()
  const query = useTeamMetrics()

  if (query.isPending) {
    return (
      <div className="flex flex-col">
        <PageHeader title={t('metrics.title')} description={t('metrics.description')} />
        <div className="flex flex-col gap-4 p-4">
          <SkeletonCards count={6} />
          <SkeletonRows rows={8} />
        </div>
      </div>
    )
  }

  if (query.isError) {
    return (
      <div className="flex flex-col">
        <PageHeader title={t('metrics.title')} description={t('metrics.description')} />
        <div className="p-4">
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </div>
      </div>
    )
  }

  const { overview, by_company: empresas, by_owner: personas, window_days: ventana } = query.data
  const ventanaTexto = t('metrics.window', { count: ventana })

  return (
    <div className="flex flex-col">
      <PageHeader title={t('metrics.title')} description={t('metrics.description')} />

      <div className="flex flex-col gap-5 p-4">
        {/* ------------------------------------------------------- el total */}
        <section className="flex flex-col gap-2">
          <h2 className="font-display text-base text-text-1">{t('metrics.overview.title')}</h2>
          <div className="flex flex-wrap gap-2">
            <MetricTile
              icon={<CircleDashed size={12} aria-hidden="true" />}
              label={t('metrics.tile.activeProjects')}
              value={overview.active_projects}
              hint={t('metrics.tile.activeProjectsHint')}
            />
            <MetricTile
              icon={<AlertTriangle size={12} aria-hidden="true" />}
              label={t('metrics.tile.overdueProjects')}
              value={overview.overdue_projects}
              hint={t('metrics.tile.overdueProjectsHint')}
              tone={overview.overdue_projects > 0 ? 'overdue' : 'neutral'}
            />
            <MetricTile
              icon={<ListTodo size={12} aria-hidden="true" />}
              label={t('metrics.tile.openTasks')}
              value={overview.open_tasks}
              hint={t('metrics.tile.openTasksHint')}
            />
            <MetricTile
              icon={<AlertTriangle size={12} aria-hidden="true" />}
              label={t('metrics.tile.overdueTasks')}
              value={overview.overdue_tasks}
              hint={t('metrics.tile.overdueTasksHint')}
              tone={overview.overdue_tasks > 0 ? 'overdue' : 'neutral'}
            />
            <MetricTile
              icon={<UserX size={12} aria-hidden="true" />}
              label={t('metrics.tile.withoutOwner')}
              value={overview.projects_without_owner}
              hint={t('metrics.tile.withoutOwnerHint')}
              tone={overview.projects_without_owner > 0 ? 'overdue' : 'neutral'}
            />
            <MetricTile
              icon={<CheckCircle2 size={12} aria-hidden="true" />}
              label={t('metrics.tile.delivered')}
              value={overview.delivered_recent}
              hint={ventanaTexto}
              tone="done"
            />
          </div>
        </section>

        {/* ------------------------------------------------- las tendencias */}
        {/*
          Va SEGUNDA, pegada a los totales: los seis números de arriba dicen
          cómo está hoy y la pregunta que sigue —siempre— es si eso viene
          mejorando o empeorando. Los cortes por empresa y por persona contestan
          otra cosa (quién y dónde), y por eso quedan abajo.
        */}
        <section className="flex flex-col gap-2">
          <div className="flex flex-col gap-0.5">
            <h2 className="font-display text-base text-text-1">{t('metrics.trends.title')}</h2>
          </div>
          <TrendsSection />
        </section>

        {/* --------------------------------------------------- por empresa */}
        <section className="flex flex-col gap-2">
          <h2 className="font-display text-base text-text-1">{t('metrics.byCompany.title')}</h2>
          {empresas.length === 0 ? (
            <EmptyState
              title={t('metrics.empty.title')}
              description={t('metrics.empty.description')}
            />
          ) : (
            <CompanyTable rows={empresas} />
          )}
        </section>

        {/* --------------------------------------------------- por persona */}
        <section className="flex flex-col gap-2">
          <h2 className="font-display text-base text-text-1">{t('metrics.byOwner.title')}</h2>
          <p className="text-2xs text-text-3">{t('metrics.byOwner.hint', { count: ventana })}</p>
          {personas.length === 0 ? (
            <EmptyState
              title={t('metrics.empty.title')}
              description={t('metrics.empty.description')}
            />
          ) : (
            <>
              <WorkloadChart rows={personas} />
              <OwnerTable rows={personas} />
            </>
          )}
        </section>
      </div>
    </div>
  )
}

function CompanyTable({ rows }: { rows: CompanyMetrics[] }) {
  const t = useT()

  return (
    <table className="hairline w-full overflow-hidden rounded-[var(--radius-card)] bg-surface-1 text-xs">
      <thead>
        <tr className="hairline-b text-2xs text-text-3">
          <th className="px-3 py-1.5 text-left font-normal">{t('common.label.company')}</th>
          <th className="px-3 py-1.5 text-right font-normal">{t('metrics.col.activeProjects')}</th>
          <th className="px-3 py-1.5 text-right font-normal">{t('metrics.col.atRisk')}</th>
          <th className="px-3 py-1.5 text-right font-normal">{t('metrics.col.openTasks')}</th>
          <th className="px-3 py-1.5 text-right font-normal">{t('metrics.col.overdueTasks')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.company_id} className="hairline-b last:border-b-0 hover:bg-surface-2">
            <td className="px-3 py-1.5">
              <span className="flex items-center gap-2">
                <CompanySpine
                  hue={row.company_color_hue}
                  label={row.company_name}
                  className="h-4"
                />
                <Link
                  to={`/companies/${row.company_id}`}
                  className="text-text-1 hover:text-signal hover:underline"
                >
                  {row.company_name}
                </Link>
              </span>
            </td>
            <td className="num px-3 py-1.5 text-right">{row.active_projects}</td>
            <td className="num px-3 py-1.5 text-right">
              <AtRisk count={row.at_risk_projects} />
            </td>
            <td className="num px-3 py-1.5 text-right">{row.open_tasks}</td>
            <td className="num px-3 py-1.5 text-right">
              <Overdue count={row.overdue_tasks} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function OwnerTable({ rows }: { rows: OwnerMetrics[] }) {
  const t = useT()

  return (
    <table className="hairline w-full overflow-hidden rounded-[var(--radius-card)] bg-surface-1 text-xs">
      <thead>
        <tr className="hairline-b text-2xs text-text-3">
          <th className="px-3 py-1.5 text-left font-normal">{t('common.label.owner')}</th>
          <th className="px-3 py-1.5 text-right font-normal">{t('metrics.col.activeProjects')}</th>
          <th className="px-3 py-1.5 text-right font-normal">{t('metrics.col.openTasks')}</th>
          <th className="px-3 py-1.5 text-right font-normal">{t('metrics.col.overdueTasks')}</th>
          <th className="px-3 py-1.5 text-right font-normal">{t('metrics.col.closed')}</th>
          <th className="px-3 py-1.5 text-right font-normal">{t('metrics.col.onTime')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.user_id ?? 'sin-responsable'}
            className="hairline-b last:border-b-0 hover:bg-surface-2"
          >
            <td className="px-3 py-1.5">
              <span className="flex items-center gap-2">
                {row.user_id ? <UserAvatar user={{ display_name: row.display_name }} /> : null}
                <span className={row.user_id ? 'text-text-1' : 'text-text-3 italic'}>
                  {row.user_id ? row.display_name : t('common.label.noOwner')}
                </span>
              </span>
            </td>
            <td className="num px-3 py-1.5 text-right">{row.active_projects}</td>
            <td className="num px-3 py-1.5 text-right">{row.open_tasks}</td>
            <td className="num px-3 py-1.5 text-right">
              <Overdue count={row.overdue_tasks} />
            </td>
            <td className="num px-3 py-1.5 text-right">{row.closed_recent}</td>
            <td className="num px-3 py-1.5 text-right">
              <OnTime rate={row.on_time_rate} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** Cero no se pinta de rojo ni lleva ícono: cero vencidas es una buena noticia. */
function Overdue({ count }: { count: number }) {
  const t = useT()
  if (count === 0) return <span className="text-text-3">0</span>
  return (
    <span
      className="inline-flex items-center gap-1"
      style={{ color: 'hsl(var(--overdue))' }}
      title={t('metrics.col.overdueTasks')}
    >
      <AlertTriangle size={11} aria-hidden="true" />
      {count}
    </span>
  )
}

function AtRisk({ count }: { count: number }) {
  if (count === 0) return <span className="text-text-3">0</span>
  return <span style={{ color: 'hsl(var(--due-soon))' }}>{count}</span>
}

/**
 * EL CASO QUE NO ES CERO.
 *
 * `null` significa que esa persona no cerró ninguna tarea **con plazo** en la
 * ventana, así que el cumplimiento no se puede calcular. Dibujar `0%` sería
 * afirmar que no cumplió nada, que es una acusación que el dato no sostiene.
 */
function OnTime({ rate }: { rate: number | null }) {
  const t = useT()
  if (rate === null) {
    return (
      <span className="text-text-3" title={t('metrics.col.onTimeUnknownHint')}>
        {t('metrics.col.onTimeUnknown')}
      </span>
    )
  }
  const tone = rate >= 80 ? '--phase-done' : rate >= 50 ? '--due-soon' : '--overdue'
  return <span style={{ color: `hsl(var(${tone}))` }}>{rate}%</span>
}

/**
 * QUIÉN ESTÁ MÁS CARGADO — y cuánta de esa carga ya se pasó de fecha.
 *
 * La tabla de abajo tiene los mismos números y contesta otra cosa: sirve para
 * mirar a una persona, no para comparar a cinco. Cinco columnas de cifras
 * alineadas exigen leer todas y ordenarlas de memoria; el largo de una barra se
 * compara sin leer nada, que es justo lo que pide la pregunta "¿quién está
 * más cargado que el resto?".
 *
 * Una sola barra por persona y no dos: el largo total **es** la carga, y el
 * tramo rojo dice qué parte de esa carga ya está vencida. Con dos barras
 * separadas habría que sumarlas mentalmente para volver a la pregunta original.
 *
 * Entra también quien no tiene a nadie asignado (`user_id: null`): "nadie está
 * mirando estas siete tareas" es exactamente el tipo de cosa que este gráfico
 * existe para hacer visible, y esconderla dejaría la suma sin cerrar contra el
 * total de la cabecera.
 */
function WorkloadChart({ rows }: { rows: OwnerMetrics[] }) {
  const t = useT()

  const items = rows
    .filter((persona) => persona.open_tasks > 0 || persona.active_projects > 0)
    .slice()
    // De más cargado a menos: la pregunta es quién está arriba de todo.
    .sort((a, b) => b.open_tasks - a.open_tasks || b.overdue_tasks - a.overdue_tasks)
    .map((persona) => ({
      id: persona.user_id ?? 'sin-responsable',
      label: persona.display_name || t('metrics.workload.unassigned'),
      valor: persona.open_tasks,
      alerta: persona.overdue_tasks,
      detalle: t(
        persona.active_projects === 1
          ? 'metrics.workload.projectsOne'
          : 'metrics.workload.projects',
        { count: persona.active_projects },
      ),
      prefijo:
        persona.user_id === null ? undefined : (
          <UserAvatar user={{ display_name: persona.display_name }} />
        ),
    }))

  if (items.length === 0) return null

  return (
    <div className="hairline flex flex-col gap-3 rounded-[var(--radius-card)] bg-surface-1 p-3">
      <header className="flex flex-col gap-0.5">
        <h3 className="text-sm text-text-1">{t('metrics.workload.title')}</h3>
        <p className="text-2xs text-text-3">{t('metrics.workload.hint')}</p>
      </header>
      <BarList
        items={items}
        resumenAccesible={t('metrics.workload.summary')}
        leyenda={{ serie: t('metrics.workload.open'), alerta: t('metrics.workload.overdue') }}
      />
    </div>
  )
}
